import React, { useState, useEffect } from 'react';
import { 
  BookingCommissionWithResource, 
  getBookingCommissions, 
  calculateAndSaveCommissions,
  getBookingSalesBeforeDiscountsAndTax,
  getCommissionResourcesFromBooking
} from '../../lib/bookingCommissions';

interface BookingCommissionSectionProps {
  booking: any;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
}

export default function BookingCommissionSection({ 
  booking, 
  onUpdate, 
  onError 
}: BookingCommissionSectionProps) {
  const [commissions, setCommissions] = useState<BookingCommissionWithResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // Check if there are commission-based resources
  const commissionResources = getCommissionResourcesFromBooking(booking);
  const hasCommissionResources = commissionResources.length > 0;

  // Calculate commission amounts
  const handleCalculateCommissions = async () => {
    try {
      setIsCalculating(true);
      const salesAmount = getBookingSalesBeforeDiscountsAndTax(booking);
      
      await calculateAndSaveCommissions(
        booking.id,
        booking.business_id,
        commissionResources,
        salesAmount
      );
      
      // Reload commission data
      const updatedCommissions = await getBookingCommissions(booking.id);
      setCommissions(updatedCommissions);
      
      await onUpdate();
    } catch (error) {
      console.error('Error calculating commissions:', error);
      onError('Failed to calculate commissions');
    } finally {
      setIsCalculating(false);
    }
  };

  // Load commission data when booking changes
  useEffect(() => {
    const loadCommissions = async () => {
      if (!booking.id) return;
      
      try {
        setLoading(true);
        const commissionData = await getBookingCommissions(booking.id);
        setCommissions(commissionData);
        
        // If no commissions exist yet, auto-calculate them
        if (commissionData.length === 0 && hasCommissionResources) {
          await handleCalculateCommissions();
        }
      } catch (error) {
        console.error('Error loading commissions:', error);
        onError('Failed to load commission data');
      } finally {
        setLoading(false);
      }
    };

    loadCommissions();
  }, [booking.id]);

  // Auto-recalculate commissions when booking financial data changes
  useEffect(() => {
    const recalculateCommissions = async () => {
      // Only recalculate if we have existing commissions and commission resources
      if (!booking.id || commissions.length === 0 || !hasCommissionResources) return;
      
      try {
        const currentSalesAmount = getBookingSalesBeforeDiscountsAndTax(booking);
        
        // Check if the sales amount has changed from the stored commissions
        const storedSalesAmount = commissions[0]?.sales_amount || 0;
        
        if (Math.abs(currentSalesAmount - storedSalesAmount) > 0.01) {
          console.log('Sales amount changed, recalculating commissions...');
          await handleCalculateCommissions();
        }
      } catch (error) {
        console.error('Error auto-recalculating commissions:', error);
      }
    };

    recalculateCommissions();
  }, [
    booking.subtotal, 
    booking.services?.length, 
    booking.resources?.length,
    JSON.stringify(booking.services?.map((s: any) => ({ id: s.id, price: s.price, quantity: s.quantity })))
  ]);

  // Don't show the section if there are no commission-based resources
  if (!hasCommissionResources) {
    return null;
  }

  const salesAmount = getBookingSalesBeforeDiscountsAndTax(booking);

  return (
    <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-gray-200 overflow-hidden shadow-lg"
         style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
      <div className="p-2 sm:p-6">
        {loading || isCalculating ? (
          <div className="text-center py-6">
            <div className="w-8 h-8 border-2 border-gray-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <div className="text-sm text-gray-600 font-medium">
              {isCalculating ? 'Calculating commissions...' : 'Loading commission data...'}
            </div>
          </div>
        ) : commissions.length === 0 ? (
          <div className="space-y-3">
            {/* Show resources with calculated commissions immediately */}
            {commissionResources.map((resource) => {
              const commissionAmount = (salesAmount * (resource.commission_rate || 0)) / 100;
              return (
                <div key={resource.id} className="backdrop-blur-xl bg-white/60 rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-sm font-semibold text-gray-900">
                          {resource.first_name} {resource.last_name}
                        </div>
                        <div className="text-xs text-gray-600 font-medium">
                          {resource.commission_rate || 0}% commission
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          ${commissionAmount.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {/* Commission Details */}
            {commissions.map((commission) => (
              <div 
                key={commission.id}
                className="backdrop-blur-xl bg-white/60 rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
                style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
              >
                <div className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {commission.resource.first_name} {commission.resource.last_name}
                      </div>
                      <div className="text-xs text-gray-600 font-medium">
                        {commission.commission_percentage}% commission
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">
                        ${commission.commission_amount.toFixed(2)}
                      </div>
                      <div className="text-xs text-green-600 font-medium capitalize bg-green-50 px-2 py-1 rounded-lg">
                        {commission.status}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 